import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowUpRight,
  CheckCircle2,
  Church,
  LoaderCircle,
  Mail,
  MapPin,
  Phone,
  Send,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

type ContactFormData = {
  name: string;
  email: string;
  phone: string;
  topic: string;
  message: string;
  website: string;
};

const topics = [
  { value: "general", label: "Consultas generales" },
  { value: "ministerios", label: "Ministerios y departamentos" },
  { value: "red-pastoral", label: "Red Pastoral Juvenil" },
  { value: "misiones", label: "Misiones" },
  { value: "mm33", label: "Iniciativa MM33" },
  { value: "directorio", label: "Directorio y localizador de iglesias" },
  { value: "credenciales", label: "Credenciales ministeriales" },
  { value: "recursos", label: "Publicaciones y recursos" },
  { value: "donaciones", label: "Ofrendas y donaciones" },
  { value: "comunicaciones", label: "Prensa y comunicaciones" },
];

const initialForm = (): ContactFormData => {
  const requestedTopic = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("tema") || ""
    : "";
  const topic = topics.some((item) => item.value === requestedTopic) ? requestedTopic : "";

  return {
    name: "",
    email: "",
    phone: "",
    topic,
    message: "",
    website: "",
  };
};

export default function ContactPage() {
  const [formData, setFormData] = useState<ContactFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const startedAt = useRef(Date.now());

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);

    const endpoint = window.ACAD_REACT_CONFIG?.contactEndpoint
      || homeUrl("wp-json/acad-react/v1/contact");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, startedAt: startedAt.current }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "No pudimos enviar el mensaje en este momento.");
      }

      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "No pudimos enviar el mensaje. Inténtelo nuevamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialForm());
    setIsSubmitted(false);
    setSubmitError("");
    startedAt.current = Date.now();
  };

  return (
    <div className="acad-react-page acad-contact-page">
      <Header />

      <main>
        <section className="acad-contact-hero" data-nav="dark">
          <img
            className="acad-contact-hero-image"
            src={assetUrl("national_office_facade.webp")}
            alt="Oficina Nacional de las Asambleas de Dios de Costa Rica"
            loading="eager"
            decoding="async"
          />
          <span className="acad-contact-hero-overlay" />

          <div className="acad-contact-shell acad-contact-hero-inner">
            <div className="acad-contact-hero-copy">
              <span className="acad-contact-eyebrow acad-contact-eyebrow--light">
                Oficina Nacional · ACAD Costa Rica
              </span>
              <h1>Hablemos.</h1>
              <p>
                Estamos para escucharte, orientarte y ayudarte a conectar con el área correcta.
              </p>
            </div>

            <a className="acad-contact-scroll" href="#contacto-formulario">
              <span>Escribir un mensaje</span>
              <i><ArrowDown aria-hidden="true" /></i>
            </a>
          </div>
        </section>

        <section className="acad-contact-details" aria-label="Información de contacto">
          <div className="acad-contact-shell acad-contact-detail-grid">
            <a href="mailto:info@acadcr.org" className="acad-contact-detail">
              <i><Mail aria-hidden="true" /></i>
              <span><small>Correo electrónico</small><strong>info@acadcr.org</strong></span>
              <ArrowUpRight aria-hidden="true" />
            </a>
            <a href="tel:+50640809890" className="acad-contact-detail">
              <i><Phone aria-hidden="true" /></i>
              <span><small>Oficina Nacional</small><strong>+506 4080-9890</strong></span>
              <ArrowUpRight aria-hidden="true" />
            </a>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Asambleas+de+Dios+Costa+Rica+Moravia"
              target="_blank"
              rel="noreferrer"
              className="acad-contact-detail"
            >
              <i><MapPin aria-hidden="true" /></i>
              <span><small>Ubicación</small><strong>Moravia, San José</strong></span>
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="acad-contact-form-section" id="contacto-formulario">
          <div className="acad-contact-shell acad-contact-form-grid">
            <div className="acad-contact-form-intro">
              <span className="acad-contact-eyebrow">Contacto directo</span>
              <h2>¿Cómo podemos ayudarte?</h2>
              <p>
                Completa el formulario y tu consulta llegará a la Oficina Nacional. Selecciona
                el tema para dirigirla al área correspondiente.
              </p>

              <div className="acad-contact-help-card">
                <Church aria-hidden="true" />
                <div>
                  <strong>¿Buscas una iglesia cercana?</strong>
                  <p>Utiliza el localizador del Home para explorar congregaciones en Costa Rica.</p>
                  <a href={homeUrl("#buscar-iglesia")}>
                    Buscar una iglesia <ArrowUpRight aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>

            <div className="acad-contact-form-wrap">
              {isSubmitted ? (
                <div className="acad-contact-success" role="status">
                  <i><CheckCircle2 aria-hidden="true" /></i>
                  <span className="acad-contact-eyebrow">Mensaje recibido</span>
                  <h2>Gracias por escribirnos.</h2>
                  <p>
                    Tu mensaje fue enviado a la Oficina Nacional. Nos pondremos en contacto
                    contigo tan pronto como sea posible.
                  </p>
                  <button type="button" onClick={resetForm}>Enviar otro mensaje</button>
                </div>
              ) : (
                <form className="acad-contact-form" onSubmit={handleSubmit}>
                  <div className="acad-contact-field acad-contact-field--full">
                    <label htmlFor="contact-name">Nombre completo</label>
                    <input
                      id="contact-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      minLength={2}
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className="acad-contact-field">
                    <label htmlFor="contact-email">Correo electrónico</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div className="acad-contact-field">
                    <label htmlFor="contact-phone">Teléfono <span>Opcional</span></label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                    />
                  </div>

                  <div className="acad-contact-field acad-contact-field--full">
                    <label htmlFor="contact-topic">Tema de la consulta</label>
                    <select
                      id="contact-topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleccione un tema</option>
                      {topics.map((topic) => (
                        <option value={topic.value} key={topic.value}>{topic.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="acad-contact-field acad-contact-field--full">
                    <label htmlFor="contact-message">Mensaje</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      minLength={10}
                      rows={6}
                      required
                    />
                  </div>

                  <div className="acad-contact-honeypot" aria-hidden="true">
                    <label htmlFor="contact-website">Sitio web</label>
                    <input
                      id="contact-website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {submitError && (
                    <p className="acad-contact-error" role="alert">{submitError}</p>
                  )}

                  <div className="acad-contact-form-bottom">
                    <p>Al enviar, autorizas a ACAD a responder esta consulta por correo o teléfono.</p>
                    <button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <><LoaderCircle className="acad-contact-spinner" aria-hidden="true" /> Enviando</>
                      ) : (
                        <>Enviar mensaje <Send aria-hidden="true" /></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="acad-contact-closing">
          <div className="acad-contact-shell acad-contact-closing-inner">
            <span>Estamos para servir</span>
            <h2>Conectamos personas, iglesias y ministerios.</h2>
            <a href="mailto:info@acadcr.org">
              info@acadcr.org <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
